
/**
 * useCustomerOrders - Hook to fetch customer orders (v2)
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CustomerResource, OrderWithRelations } from '../../core/resources/customer';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface UseCustomerOrdersOptions {
    customerId?: string | null;
    enabled?: boolean;
}

export interface UseCustomerOrdersResult {
    data: { orders: OrderWithRelations[] } | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

export function useCustomerOrders(options: UseCustomerOrdersOptions = {}): UseCustomerOrdersResult {
    const { storeId } = usePluginConfig();
    const { isSessionInitialized } = useTagadaContext();

    const { customerId = null, enabled = true } = options;

    // Create customer resource client
    const customerResource = useMemo(() => {
        try {
            return new CustomerResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(
                'Failed to initialize customer resource: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
        }
    }, []);

    // Determine if query should be enabled
    const isEnabled = useMemo(() => {
        return Boolean(enabled && customerId && storeId && isSessionInitialized);
    }, [enabled, customerId, storeId, isSessionInitialized]);

    // Create query key
    const queryKey = useMemo(
        () => ['customer-orders', { customerId, storeId }],
        [customerId, storeId]
    );

    // Fetch customer orders using TanStack Query
    const {
        data = null,
        isLoading,
        error,
        refetch,
    } = useQuery<{ orders: OrderWithRelations[] } | null>({
        queryKey,
        enabled: isEnabled,
        queryFn: async () => {
            if (!customerId || !storeId) {
                return null;
            }

            return await customerResource.getCustomerOrders(customerId, storeId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    return {
        data,
        isLoading,
        error,
        refetch: async () => {
            await refetch();
        },
    };
}

